import { z } from "zod";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  GOOGLE,
  LINKEDIN,
  META,
  PLATFORMS_SUPPORTED,
  TIKTOK,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_IG_REQUIRED,
} from "../../../../utils/constants";
import i18next from "i18next";
import {
  FIELD_MIN_DAILY_BUDGET,
  FIELD_REQUIRED,
} from "../../../../i18n/keysTranslations";
import { metaCampaignSchema } from "./platforms/metaCampaignSchema";
import { tiktokCampaignSchema } from "./platforms/tiktokCampaignSchema";
import {
  googleCampaignSchema,
  validateGoogleDsaSettings,
} from "./platforms/googleCampaignSchema";
import { linkedinCampaignSchema } from "./platforms/linkedinCampaignSchema";
import { adGroupSchema } from "./adgroup/adGroupGeneralSchema";
import { assetsGroupSchema } from "./assetsGroup/assetsGroupGeneralSchema";
import { getIntegrationKey } from "../../../../actions/getters";
import { verifyTikTokBudget } from "./adgroup/adGroupTikTokSchema";
import { getMinimumBudget } from "../../../../services/adsCampaigns";

export const getCampaignSchema = (context) => {
  const hasContext = Boolean(
    context.businessName && context.businessDescription
  );
  return z
    .object({
      context: hasContext
        ? z.string().optional()
        : z.string().min(1, { message: i18next.t(FIELD_REQUIRED) }),
      language: z.string().min(1, { message: i18next.t(FIELD_REQUIRED) }),
      locations: z.object(
        {
          locations: z
            .array(z.any(), {
              message: i18next.t(FIELD_REQUIRED),
            })
            .min(1, {
              message: i18next.t(FIELD_REQUIRED),
            }),
          contexts: z.array(z.string()).optional(),
        },
        {
          message: i18next.t(FIELD_REQUIRED),
        }
      ),
      platforms: z.array(z.enum(PLATFORMS_SUPPORTED)).default([]),
      states: z.any().optional(),

      platformsData: z
        .object({
          [META]: metaCampaignSchema.optional(),
          [GOOGLE]: googleCampaignSchema.optional(),
          [TIKTOK]: tiktokCampaignSchema.optional(),
          [LINKEDIN]: linkedinCampaignSchema.optional(),
        })
        .optional(),

      report: z.any().optional(),

      adsGroups: z.array(z.any()).default([]),
      assetsGroups: z.array(z.any()).default([]),
    })
    .superRefine((data, ctx) => {
      const integrations = context.integrations || {};
      const platforms = data.platforms;

      const adGroups = data.adsGroups;

      adGroups.forEach((adGroup, index) => {
        const safeAdGroup = adGroupSchema({
          campaign: data,
          integrations,
          indexAdGroup: index,
          currencyTaxes: context.currencyTaxes,
        }).safeParse(adGroup);
        if (!safeAdGroup.success) {
          safeAdGroup.error.issues.forEach((issue) => {
            ctx.addIssue(issue);
          });
        }
      });

      const assetsGroups = data.assetsGroups;
      assetsGroups.forEach((assetsGroup, index) => {
        const safeAssetsGroup = assetsGroupSchema({
          campaign: data,
          integrations,
          assetsGroupID: index,
        }).safeParse(assetsGroup);
        if (!safeAssetsGroup.success) {
          safeAssetsGroup.error.issues.forEach((issue) => {
            ctx.addIssue(issue);
          });
        }
      });

      if (platforms.includes(TIKTOK)) {
        const platformData = data.platformsData?.[TIKTOK];
        const useCampaignBudget = platformData?.useCampaignBudget;
        if (useCampaignBudget) {
          const integrationKey = getIntegrationKey(TIKTOK, true);
          const integrationID = platformData?.integrationID;
          const typeBudget = platformData?.typeBudget;
          const budget = platformData?.budget;

          const budgetValidation = verifyTikTokBudget({
            useCampaignBudget: true,
            typeBudget,
            budget,
            integrations,
            integrationID,
            integrationKey,
          });

          if (!budgetValidation.ok) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["platformsData", TIKTOK, "budget"],
              message: i18next.t(budgetValidation.message, {
                value: budgetValidation.value,
                dailyValue: budgetValidation.dailyValue,
                currency: budgetValidation.currency,
              }),
            });
          }
        }
      }

      if (platforms.includes(GOOGLE)) {
        const platformData = data.platformsData?.[GOOGLE];

        const hasDsaAdGroups = (data.adsGroups || []).some(
          (ag) => ag.platform === GOOGLE && ag.type === "dsa"
        );
        validateGoogleDsaSettings({
          path: ["platformsData", GOOGLE],
          data: platformData,
          ctx,
          hasDsaAdGroups,
        });

        const budget = platformData?.budget;
        const numericBudget = Number(budget || 0);
        const integrationID = platformData?.integrationID;
        const adAccountID = platformData?.adAccountID;
        const integrationGoogle = integrations[GOOGLE]?.[integrationID];
        const advertiserData = integrationGoogle?.advertisers?.[adAccountID];
        const currency = advertiserData?.currency;
        const currencyTax = context.currencyTaxes?.[currency] || 1;

        const minimumBudget = getMinimumBudget({
          platform: GOOGLE,
          typeBudget: ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
          currency: currency,
          currencyTax,
        });

        if (numericBudget < minimumBudget) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["platformsData", GOOGLE, "budget"],
            message: i18next.t(FIELD_MIN_DAILY_BUDGET, {
              value: minimumBudget,
              currency,
            }),
          });
        }
      }

      if (platforms.includes(LINKEDIN)) {
        const platformData = data.platformsData?.[LINKEDIN];
        const budget = platformData?.budget;
        const numericBudget = Number(budget || 0);
        const integrationID = platformData?.integrationID;
        const adAccountID = platformData?.adAccountID;
        const integrationLinkedin = integrations[LINKEDIN]?.[integrationID];
        const advertiserData = integrationLinkedin?.advertisers?.[adAccountID];
        const currency = advertiserData?.currency;
        const currencyTax = context.currencyTaxes?.[currency] || 1;

        const minimumBudget = getMinimumBudget({
          platform: LINKEDIN,
          typeBudget: platformData?.typeBudget,
          startDateTime: platformData?.startDate,
          endDateTime: platformData?.endDate,
          currency,
          currencyTax,
        });

        if (minimumBudget != null && numericBudget < minimumBudget) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["platformsData", LINKEDIN, "budget"],
            message: i18next.t(FIELD_MIN_DAILY_BUDGET, {
              value: minimumBudget,
              currency: currency || "USD",
            }),
          });
        }
      }

      if (platforms.includes(META)) {
        const platformData = data.platformsData?.[META];
        const instagramID = platformData?.instagramID;
        const adGroups = data.adsGroups || [];

        // Verificar si algún adGroup de Meta tiene un destinationType que requiere IG
        const hasAdGroupRequiringIG = adGroups.some(
          (adGroup) =>
            adGroup?.platform === META &&
            adGroup?.destinationType &&
            ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_IG_REQUIRED.includes(
              adGroup.destinationType
            )
        );

        if (hasAdGroupRequiringIG && !instagramID) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["platformsData", META, "instagramID"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
      }
    });
};
